import { useEffect, useRef, useState } from "react"
import { Portal } from "./portal"
import { useDropdownPosition } from "./dropdown-position"

interface SelectProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: readonly string[]
  disabled?: boolean
  placeholder?: string
  emptyMessage?: string
  clearOption?: string
  searchable?: boolean
  searchPlaceholder?: string
}

export function Select({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  emptyMessage = "No options available.",
  clearOption,
  searchable = false,
  searchPlaceholder = "Search options...",
}: SelectProps) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [query, setQuery] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const position = useDropdownPosition(buttonRef, open)
  const selectedIndex = value ? Math.max(0, options.indexOf(value) + (clearOption ? 1 : 0)) : 0
  const filteredOptions = searchable
    ? options.filter((option) => option.toLowerCase().includes(query.trim().toLowerCase()))
    : options
  const displayOptions = clearOption ? [clearOption, ...filteredOptions] : filteredOptions

  useEffect(() => {
    if (!open) return

    function handleOutsidePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false)
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown)
  }, [open])

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return

    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      setActiveIndex(selectedIndex)
      setOpen(true)
      return
    }

    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      const direction = event.key === "ArrowDown" ? 1 : -1
      setActiveIndex((index) => Math.min(displayOptions.length - 1, Math.max(0, index + direction)))
      return
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      setOpen(true)
      setActiveIndex(event.key === "Home" ? 0 : Math.max(0, displayOptions.length - 1))
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setOpen(false)
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (open) {
        const selected = displayOptions[activeIndex]
        if (selected) onChange(clearOption && selected === clearOption ? "" : selected)
        setOpen(false)
      } else {
        setActiveIndex(selectedIndex)
        setOpen(true)
      }
    }
  }

  const displayValue = value || placeholder || "Select..."

  return (
    <div ref={containerRef} className="relative">
      {label && <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setActiveIndex(selectedIndex)
          setQuery("")
          setOpen((isOpen) => !isOpen)
        }}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center justify-between rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-forest-700 focus:outline-none disabled:opacity-60 ${
          value ? "text-ink" : "text-ink/40"
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <svg
          className={`ml-2 h-4 w-4 shrink-0 text-ink/50 transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`}
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden
        >
          <path d="m1 1.5 5 5 5-5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && position && (
        <Portal>
          <div
            ref={menuRef}
            role="listbox"
            style={{ left: position.left, width: position.width, maxHeight: position.maxHeight, top: position.top, bottom: position.bottom }}
            className="fixed z-[60] overflow-y-auto rounded-lg border border-gold-500 bg-white p-1 shadow-lg"
          >
            {searchable && (
              <div className="sticky top-0 z-10 bg-white p-1 pb-0">
                <input
                  autoFocus
                  type="search"
                  aria-label={searchPlaceholder}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value)
                    setActiveIndex(0)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault()
                      setQuery("")
                      setOpen(false)
                      buttonRef.current?.focus()
                    }
                  }}
                  placeholder={searchPlaceholder}
                  className="mb-1 w-full rounded-md border border-ink/10 bg-cream-50 px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
              </div>
            )}
            {displayOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-ink/50">{emptyMessage}</p>
            ) : displayOptions.map((option, index) => {
              const isClearOption = clearOption === option
              const isSelected = isClearOption ? !value : option === value
              const isActive = index === activeIndex
              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    onChange(isClearOption ? "" : option)
                    setActiveIndex(index)
                    setOpen(false)
                    buttonRef.current?.focus()
                  }}
                  className={`block w-full rounded px-3 py-2 text-left text-sm text-ink transition-colors duration-150 ease-out ${
                    isActive ? "bg-gold-500/30" : isSelected ? "bg-gold-500/20" : "hover:bg-cream-100"
                  }`}
                >
                  {option}
                </button>
              )
            })}
          </div>
        </Portal>
      )}
    </div>
  )
}
